import { Box, Chip, Stack, Typography } from "@mui/material";
import { Colors } from "Components/Layout/Themes/Colors";
import {
  type CardTransactionItem as CardTransactionItemType,
  CardTransactionStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";
import { formatTransactionDateTime } from "Utils/DateUtils";

const PageResources = Resources.Components.CardTransactions;

type ChipColor = "success" | "error" | "warning" | "default";

const STATUS_TO_CHIP_COLOR: Record<CardTransactionStatus, ChipColor> = {
  [CardTransactionStatus.Approved]: "success",
  [CardTransactionStatus.Rejected]: "error",
  [CardTransactionStatus.Canceled]: "error",
  [CardTransactionStatus.Pending]: "warning",
  [CardTransactionStatus.Unknown]: "default",
};

type Props = {
  item: CardTransactionItemType;
  isBottomBorderHidden?: boolean;
};

export const CardTransactionItem = ({
  item: {
    occurredAt,
    terminalDisplayName,
    employeeDisplayName,
    amount,
    currency,
    status,
    direction,
    isRefundOrCancellation,
    cardBrand,
    truncatedPAN,
  },
  isBottomBorderHidden,
}: Props) => {
  const { t } = useResource();

  const bottomBorder = isBottomBorderHidden
    ? undefined
    : `1px solid ${Colors.MediumGrey}`;

  return (
    <Box
      display="flex"
      width="100%"
      mt={2.5}
      pb={1.5}
      borderBottom={bottomBorder}
      justifyContent="space-between"
      gap={2}
    >
      <Stack gap={0.5} textAlign="left">
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Chip
            size="small"
            color={STATUS_TO_CHIP_COLOR[status]}
            label={t(PageResources.Status[status])}
          />
          {isRefundOrCancellation && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={t(PageResources.Direction[direction])}
            />
          )}
        </Box>
        {!!terminalDisplayName && (
          <Typography fontSize="0.875rem">
            {`${t(PageResources.Columns.Terminal)}: ${terminalDisplayName}`}
          </Typography>
        )}
        {!!employeeDisplayName && (
          <Typography fontSize="0.75rem" color={Colors.TextLight}>
            {`${t(PageResources.Columns.Operator)}: ${employeeDisplayName}`}
          </Typography>
        )}
        {(!!cardBrand || !!truncatedPAN) && (
          <Typography fontSize="0.75rem" color={Colors.TextLight}>
            {[cardBrand, truncatedPAN].filter(Boolean).join(" • ")}
          </Typography>
        )}
      </Stack>
      <Stack alignItems="flex-end" gap={0.5}>
        {/* Refunds/voids carry a positive magnitude (sign lives in `direction`) — render them with the same
            "-" + red convention as outgoing bank transfers (see Shared/Transactions/AmountWithDate). */}
        <Typography
          fontWeight={600}
          color={isRefundOrCancellation ? Colors.RedDark : undefined}
        >
          {isRefundOrCancellation ? "-" : ""}
          {formatCurrency({ amount, currencyCode: currency })}
        </Typography>
        {!!occurredAt && (
          <Typography fontSize="0.75rem" color={Colors.TextLight}>
            {formatTransactionDateTime(occurredAt)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
